import React, { useState, useEffect, useRef } from "react";
import type { LearningModule, ModuleMeta, Topic } from "./types";
import ModuleStatistics from "./ModuleStatistics";
import TopicMultiSelect from "../TopicMultiSelect";
import ApiService from "../../_services/ApiService";
import { useITeachConfig } from "../../_context/PresetContext";
import CourseManagementService from "../../_services/CourseManagementService";
import type { CourseDocument } from "../../_services/CourseManagementService";

type OverviewTabProps = {
  module: LearningModule | null;
  meta: ModuleMeta;
  onMetaChange: (updates: Partial<ModuleMeta>) => void;
  onModuleChange: (updates: Partial<LearningModule>) => void;
  isViewMode: boolean;
  showAIHelper: boolean;
  setShowAIHelper: (show: boolean) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  isGenerating: boolean;
  onGenerateTopics: () => void;
  isEditMode: boolean;
  createdDate?: string;
  modifiedDate?: string;
};

export default function OverviewTab({
  module,
  meta,
  onMetaChange,
  onModuleChange,
  isViewMode,
  showAIHelper,
  setShowAIHelper,
  prompt,
  setPrompt,
  isGenerating,
  onGenerateTopics,
  isEditMode,
  createdDate,
  modifiedDate,
}: OverviewTabProps) {
  const { config } = useITeachConfig();

  const handleTopicsChange = (topics: Topic[]) => {
    if (!module) return;
    onModuleChange({ selectedTopics: topics.map(t => t.id) });
  };

  // Sub-type options loaded from study_resource_sub_type_master
  const [subTypes, setSubTypes] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const typeId = module?.lessonPlanType === 'lesson_plan' ? 25 : 15;
    ApiService.getStudyResourceSubTypes(typeId)
      .then((res) => {
        const data = (res.data as any) || [];
        setSubTypes(Array.isArray(data) ? data : []);
        // Auto-select if only one sub-type or no selection yet
        if (!module?.lessonPlanSubTypeId && Array.isArray(data) && data.length > 0) {
          onModuleChange({ lessonPlanSubTypeId: data[0].id });
        }
      })
      .catch((err) => console.error('[OverviewTab] Error fetching sub-types:', err));
  }, [module?.lessonPlanType]);

  // Get selected topic objects for display
  const [selectedTopicObjects, setSelectedTopicObjects] = useState<Topic[]>([]);

  useEffect(() => {
    const loadSelectedTopics = async () => {
      if (!module || !module.selectedTopics || module.selectedTopics.length === 0) {
        setSelectedTopicObjects([]);
        return;
      }
      try {
        // Fetch actual topic details from backend
        const response = await ApiService.getTopicsByIds(module.selectedTopics);
        const responseData = response.data as any;
        const topics = responseData?.data || responseData || [];
        setSelectedTopicObjects(Array.isArray(topics) ? topics : []);
      } catch (error) {
        console.error('[OverviewTab] Error fetching topic details:', error);
        // Fallback to showing IDs if fetch fails
        const topicObjects = module.selectedTopics.map(id => ({
          id,
          name: `Topic ${id}`,
        }));
        setSelectedTopicObjects(topicObjects);
      }
    };
    loadSelectedTopics();
  }, [module?.selectedTopics]);

  // Fetch course documents linked to selected topics (for AI Assistant context)
  const [courseDocuments, setCourseDocuments] = useState<CourseDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const promptAutoFilledRef = useRef(false);

  useEffect(() => {
    const fetchCourseDocuments = async () => {
      if (!selectedTopicObjects || selectedTopicObjects.length === 0) {
        setCourseDocuments([]);
        return;
      }

      // Extract unique courseIds from selected topics
      const courseIds = Array.from(new Set(
        selectedTopicObjects
          .map(t => t.courseId)
          .filter((id): id is number => id != null)
      ));

      if (courseIds.length === 0) {
        setCourseDocuments([]);
        return;
      }

      setLoadingDocs(true);
      try {
        const allDocs: CourseDocument[] = [];
        for (const courseId of courseIds) {
          const response = await CourseManagementService.getCourseDocuments(courseId);
          if (response.success && response.data?.documents) {
            allDocs.push(...response.data.documents);
          }
        }
        setCourseDocuments(allDocs);

        // Pre-populate the AI prompt if documents found and prompt is still empty
        if (allDocs.length > 0 && !prompt.trim() && !promptAutoFilledRef.current) {
          promptAutoFilledRef.current = true;
          setPrompt(
            "Based on the uploaded course documents, generate a comprehensive learning module with chapters and topics that cover the syllabus thoroughly."
          );
        }
      } catch (err) {
        console.error('[OverviewTab] Error fetching course documents:', err);
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchCourseDocuments();
  }, [selectedTopicObjects]);

  return (
    <div className="lmb-overview-tab">
      {/* Module Information Card */}
      <div className="lmb-card">
        <div className="lmb-card-hd">
          <div><strong>📋 Module Information</strong></div>
        </div>
        <div className="lmb-card-bd">
          <div className="lmb-grid-3">
            <div>
              <label className="lmb-muted lmb-label">
                Title <span style={{ color: "red" }}>*</span>
              </label>
              <input
                className="lmb-input"
                value={meta.title}
                onChange={(e) => onMetaChange({ title: e.target.value })}
                placeholder="Enter module title..."
                disabled={isViewMode}
                required
              />
            </div>

            <div>
              <label className="lmb-muted lmb-label">Code</label>
              <input
                className="lmb-input"
                value={module?.code || ""}
                onChange={(e) => module && onModuleChange({ code: e.target.value })}
                placeholder="Auto-generated from title"
                disabled={isViewMode}
              />
            </div>

            <div>
              <label className="lmb-muted lmb-label">Audience</label>
              <input
                className="lmb-input"
                value={meta.audience}
                onChange={(e) => onMetaChange({ audience: e.target.value })}
                placeholder="e.g., Developers, Students..."
                disabled={isViewMode}
              />
            </div>

            <div>
              <label className="lmb-muted lmb-label">Difficulty</label>
              <select
                className="lmb-select"
                value={meta.difficulty}
                onChange={(e) => onMetaChange({ difficulty: e.target.value as ModuleMeta["difficulty"] })}
                disabled={isViewMode}
              >
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
            </div>

            <div>
              <label className="lmb-muted lmb-label">Estimated Duration (mins)</label>
              <input
                className="lmb-input"
                type="number"
                min={5}
                step={5}
                value={meta.durationMins}
                onChange={(e) => onMetaChange({ durationMins: Math.max(5, Number(e.target.value) || 5) })}
                disabled={isViewMode}
              />
            </div>

            <div>
              <label className="lmb-muted lmb-label">Tone</label>
              <select
                className="lmb-select"
                value={meta.tone}
                onChange={(e) => onMetaChange({ tone: e.target.value as ModuleMeta["tone"] })}
                disabled={isViewMode}
              >
                <option>Practical</option>
                <option>Friendly</option>
                <option>Formal</option>
                <option>Academic</option>
              </select>
            </div>

            <div>
              <label className="lmb-muted lmb-label">
                Type <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="lmb-select"
                value={module?.lessonPlanType || 'learning_module'}
                onChange={(e) => {
                  if (!module) return;
                  const val = e.target.value as 'learning_module' | 'lesson_plan';
                  const updates: Partial<LearningModule> = { lessonPlanType: val, lessonPlanSubTypeId: undefined };
                  if (val === 'learning_module') {
                    updates.selectedTopics = [];
                    updates.selectedBatches = [];
                  }
                  onModuleChange(updates);
                }}
                disabled={isViewMode || !!config.lmBuilder?.lessonPlanTypeRestrictTo || (module?.id !== undefined && module?.id !== null)}
              >
                <option value="learning_module">Learning Module</option>
                <option value="lesson_plan">Lesson Plan</option>
              </select>
            </div>

            <div>
              <label className="lmb-muted lmb-label">
                Sub Type <span style={{ color: "red" }}>*</span>
              </label>
              <select
                className="lmb-select"
                value={module?.lessonPlanSubTypeId || ''}
                onChange={(e) => module && onModuleChange({ lessonPlanSubTypeId: Number(e.target.value) })}
                disabled={isViewMode}
              >
                {subTypes.map((st) => (
                  <option key={st.id} value={st.id}>{st.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="lmb-muted lmb-label">Analytics Dashboard</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={module?.analyticsDashboard === 1}
                  onChange={(e) => module && onModuleChange({ analyticsDashboard: e.target.checked ? 1 : 0 })}
                  disabled={isViewMode}
                  style={{ width: 'auto', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Include in analytics dashboard
                </span>
              </div>
            </div>

            <div>
              <label className="lmb-muted lmb-label">Flip Class</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={module?.flipClassEnabled === 1}
                  onChange={(e) => module && onModuleChange({ flipClassEnabled: e.target.checked ? 1 : 0 })}
                  disabled={isViewMode}
                  style={{ width: 'auto', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Enable flip class
                </span>
              </div>
            </div>

            <div>
              <label className="lmb-muted lmb-label">Kids Mode</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={module?.renderMode === 'kids'}
                  onChange={(e) => module && onModuleChange({ renderMode: e.target.checked ? 'kids' : undefined })}
                  disabled={isViewMode}
                  style={{ width: 'auto', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Full-screen guided view for younger students
                </span>
              </div>
            </div>
          </div>

          {module?.lessonPlanType === 'lesson_plan' && (
            <>
              {/* Topic Selection */}
              <div style={{ marginTop: 12 }}>
                <label className="lmb-muted lmb-label">
                  Courses/Topics <span style={{ color: "red" }}>*</span>
                </label>
                <TopicMultiSelect
                  selectedTopics={selectedTopicObjects}
                  onChange={handleTopicsChange}
                  placeholder="Search and select topics..."
                  disabled={isViewMode}
                />
              </div>

            </>
          )}

          <div style={{ marginTop: 12 }}>
            <label className="lmb-muted lmb-label">Description / Remarks</label>
            <textarea
              className="lmb-textarea"
              value={module?.remarks || ""}
              onChange={(e) => module && onModuleChange({ remarks: e.target.value })}
              placeholder="Brief description of this learning module..."
              rows={3}
              disabled={isViewMode}
            />
          </div>

          {/* Icon / Thumbnail Upload */}
          <IconUpload
            iconUrl={module?.icon}
            isViewMode={isViewMode}
            onSelect={(file) => module && onModuleChange({ iconFile: file, icon: URL.createObjectURL(file) })}
            onRemove={() => module && onModuleChange({ icon: '', iconFile: undefined })}
          />
        </div>
      </div>

      {/* Module Statistics - Only show in edit mode when module has data */}
      {module && isEditMode && (
        <ModuleStatistics
          module={module}
          createdDate={createdDate}
          modifiedDate={modifiedDate}
        />
      )}

      {/* AI Helper - Only show in create mode (not editing existing module) */}
      {!isViewMode && !isEditMode && (

        <div className="lmb-card">
          <div
            className="lmb-card-hd"
            style={{ cursor: "pointer" }}
            onClick={() => setShowAIHelper(!showAIHelper)}
          >
            <div><strong>✨ AI Assistant (Optional)</strong></div>
            <button className="lmb-btn lmb-btn-sm" type="button">
              {showAIHelper ? "▼ Hide" : "▶ Show"}
            </button>
          </div>
          {showAIHelper && (
            <div className="lmb-card-bd">
              <div className="lmb-prompt-hint">
                <strong>💡 Tip:</strong> Describe what you want to teach. The AI will generate <strong>chapters and sections</strong> (topics only). You can then add resources per chapter in the Builder tab.
              </div>

              {/* Show linked course documents */}
              {loadingDocs && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Loading course documents...
                </div>
              )}
              {courseDocuments.length > 0 && (
                <div style={{
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 10,
                  fontSize: 13,
                }}>
                  <strong style={{ color: '#0369a1' }}>Course Documents ({courseDocuments.length}):</strong>
                  <div style={{ marginTop: 4 }}>
                    {courseDocuments.map((doc) => (
                      <div key={doc.studyResourceId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ color: '#0369a1' }}>📄</span>
                        <span>{doc.Name}</span>
                        {doc.FileSizeInByte && (
                          <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>
                            ({(doc.FileSizeInByte / 1024).toFixed(0)} KB)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                    The AI will consult these documents when generating topics.
                  </div>
                </div>
              )}

              <label className="lmb-muted lmb-label">Enter your prompt</label>
              <textarea
                className="lmb-textarea"
                placeholder="Example: Create a learning module about React hooks for intermediate developers. Include sections on useState, useEffect, and custom hooks with practical examples..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                disabled={isGenerating}
              />
              <div style={{ marginTop: 12, textAlign: "right" }}>
                <button
                  className="lmb-btn lmb-btn-primary"
                  onClick={onGenerateTopics}
                  disabled={isGenerating || !prompt.trim()}
                  type="button"
                >
                  {isGenerating ? (
                    <>
                      <span className="lmb-loading"></span> Generating...
                    </>
                  ) : (
                    "Generate Topics with AI"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Icon / Thumbnail Upload ── */

const MAX_ICON_SIZE = 2 * 1024 * 1024; // 2 MB

function IconUpload({
  iconUrl,
  isViewMode,
  onSelect,
  onRemove,
}: {
  iconUrl?: string;
  isViewMode: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ICON_SIZE) {
      alert('Icon image must be smaller than 2 MB');
      return;
    }
    onSelect(file);
    // Reset so the same file can be re-selected if needed
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ marginTop: 12 }}>
      <label className="lmb-muted lmb-label">Icon / Thumbnail</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt="Module icon"
            style={{
              width: 64,
              height: 64,
              objectFit: 'cover',
              borderRadius: 6,
              border: '1px solid var(--border-strong)',
            }}
          />
        ) : (
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 6,
              border: '2px dashed var(--border-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-faint)',
              fontSize: 24,
            }}
          >
            🖼
          </div>
        )}

        {!isViewMode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              type="button"
              className="lmb-btn lmb-btn-sm"
              onClick={() => fileRef.current?.click()}
              style={{ fontSize: 12 }}
            >
              {iconUrl ? 'Change' : 'Upload'}
            </button>
            {iconUrl && (
              <button
                type="button"
                className="lmb-btn lmb-btn-sm"
                onClick={onRemove}
                style={{ fontSize: 12, color: 'var(--status-danger)' }}
              >
                Remove
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>Max 2 MB</span>
          </div>
        )}
      </div>
    </div>
  );
}
